<template>
    <div class="ad-page">
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && ad.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="ad"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                        />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            :edit-mode="editMode"
                            :sections="sections"
                            :item="ad"
                            :campuses="ad.campuses"
                            @on-delete-confirm="onDeleteConfirm"
                            @on-campuses-change="onCampusesChange"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import { StatusCodes as HTTP } from 'http-status-codes';
import ItemInformation from '@/components/elements/ItemInformation';

import AddEditForm from '@/components/ads/AddEditForm';

export default {
    page: {
        title: 'Manage Ads'
    },

    validations: {
        ad: {
            name: {
                required
            },
            link: {
                required
            },
            exposure: {
                required
            },
            adPositionId: {
                required
            },
            imageId: {
                required
            },
            bubbleBannerImageId: {
                requiredIf: (v, { adPosition }) =>
                    adPosition?.slot !== 'homepage-balloon' || !!v
            }
        }
    },

    components: {
        AddEditForm,
        ItemInformation
    },

    data() {
        const { id = null } = this.$route.params;

        return {
            ad: {
                campuses: []
            },
            title: id ? 'Edit ad' : 'Add ad',
            items: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Ads',
                    to: '/ads'
                },
                {
                    text: id ? 'Edit' : 'Add',
                    active: true
                }
            ],
            id,
            editMode: !!id,
            serverErrors: []
        };
    },

    computed: {
        sections() {
            const sections = {
                campuses: true,
                permalink: false,
                meta: false
            };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        }
    },

    async created() {
        if (this.editMode) {
            try {
                this.ad = await this.getAd(this.id);
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }

            return;
        }

        try {
            const { rows } = await this.getCampuses({ perPage: 5 });

            this.ad.campuses = rows;
        } catch (error) {
            console.error(error);

            this.$toasterError();
        }
    },

    methods: {
        ...mapActions({
            addAd: 'ads/store',
            deleteAd: 'ads/destroy',
            getAd: 'ads/show',
            updateAd: 'ads/update',
            getCampuses: 'campuses/index'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            const campuses = this.ad.campuses.map(campus => campus.id);

            const data = {
                ...this.ad,
                campuses
            };

            try {
                if (!this.editMode) {
                    const added = await this.addAd(data);

                    this.$router.push(`/ads/${added.id}/edit`);
                } else {
                    const { updatedAt } = await this.updateAd({
                        id: this.id,
                        data
                    });

                    this.ad.updatedAt = updatedAt;

                    this.$toaster('Ad updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteAd(this.id);

                this.$router.push('/ads');
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        onCampusesChange(campuses) {
            this.ad.campuses = campuses;
            this.informationKey++;
        }
    }
};
</script>
